/**
 * Render-site accessor for a service error's member-visible message.
 *
 * @module utils/serviceErrorMessage
 */

/**
 * Return the message a service error carries, or `undefined`.
 *
 * bl-1153-1 (absorbing bl-1153-2): every error thrown by `src/services/` is a
 * PLAIN OBJECT LITERAL — `DiscussionError`, `GovernanceDiscussionApiError`,
 * `DiscussionCommentApiError`, `ContinuationError`, `RejectionError` are all
 * `{ type|code, status?, message? }`, none of them an `Error` subclass. Render
 * sites that guarded with `err instanceof Error ? err.message : fallback` were
 * therefore taking the `false` branch UNCONDITIONALLY and the server's sentence
 * never reached a member, no matter what the service layer put in `message`.
 *
 * `Error` INSTANCES ARE DELIBERATELY EXCLUDED. A raw `TypeError: Failed to fetch`,
 * a `SyntaxError` from a truncated body, or any other thrown runtime exception
 * reaching a catch block also has a `.message`, and surfacing it would put text
 * the API never sent — potentially a URL or an internal detail — in front of a
 * member. That is the leak-escalation rule this story is bound by: only text the
 * server chose to put in its response body reaches the member. Raw exceptions
 * fall through to the caller's own copy, exactly as they did before.
 *
 * ⚠ CAVEAT (bl-1153-1 code-review): the `Error`-refusal above screens out RUNTIME
 * exceptions, not CLIENT-AUTHORED text. `apiHelpers.createTimeout` rejects with a
 * plain object literal — `{ type, message: 'Request timed out after 30000ms' }` —
 * which is indistinguishable here from a server sentence and WOULD be rendered
 * verbatim. That is not member-reachable today: `createTimeout` is raced only in
 * `discussionService.createFromCapture` / `getByCapture` / `getDiscussion`, all of
 * which have zero non-test callsites, while the three live governance functions and
 * every `discussionCommentService` function race no timeout at all. If `bl-1154`
 * revives those dead clients, that string reaches a member as developer-facing copy
 * with a raw millisecond figure — route it through a copy pass (Menley's lane)
 * BEFORE the revival, not after.
 *
 * Returns `undefined` rather than a canned string on purpose: the CALLER owns the
 * per-context fallback copy ("Discussion not found" reads better in context than
 * anything the service layer could invent), so the shape at every call site is
 * `serviceErrorMessage(err) ?? '<this page's existing string>'`. That makes the
 * change strictly additive — the member sees the server's explanation when there
 * is one, and the exact copy they saw before when there is not.
 */
export function serviceErrorMessage(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  if (err instanceof Error) return undefined;
  // `instanceof Error` alone is ENVIRONMENT-DEPENDENT for host exceptions. Browsers
  // put `Error.prototype` on `DOMException`'s chain (WebIDL), but jsdom does NOT —
  // verified in this repo's own test environment, where an AbortError `DOMException`
  // reports `instanceof Error === false` and would otherwise leak its text. Every
  // real exception carries `stack`; no service error literal in `src/services/` does
  // (they are `{ type|code, status?, message?, existing*Id? }`). So `stack` is a
  // reliable second discriminator and closes the gap in both directions.
  if ('stack' in err) return undefined;
  if (!('message' in err)) return undefined;

  const message = (err as { message?: unknown }).message;
  if (typeof message !== 'string') return undefined;

  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
